import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppBrandMark, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote, secondaryButtonSx } from '@components'
import { BI_ROUTES } from '@constants'
import { dsColorNew } from '@palette'
import { fmtDay, fmtRange, fmtWeekday, fromIso } from '../../../@services/dates'
import { movers } from '../@services/model'
import { detailSubtitleTail } from '../../../@services/market'
import { CMP_WORD, PREV_COLUMN, STAT_LABEL, cmpPhrase, money, signedMoney, signedPct } from '../@services/format'
import { MEDIAN } from './Grid'


const box = { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', p: '12px 14px', backgroundColor: dsColorNew.white }
const overline = { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }

const captured = stats => {
	const a = fromIso(stats?.firstCapture), z = fromIso(stats?.lastCapture)
	return a && z ? `captured ${fmtRange(a, z)}` : ''
}
const pricesCount = n => `${(n || 0).toLocaleString('en-GB')} price${n === 1 ? '' : 's'}`

function Period({ title, stats, value, stat, code, thin, empty }) {
	return <Box sx={{ ...box, ...(thin && { borderColor: dsColorNew.noteAmberBorder, backgroundColor: dsColorNew.noteAmberBg }), ...(empty && { borderStyle: 'dashed' }) }}>
		<Typography sx={overline}>{title}</Typography>
		{empty
			? <>
				<Typography sx={{ fontSize: 16, fontWeight: 700, color: dsColorNew.textSubtle, mt: 0.5 }}>No comparison</Typography>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textDark, mt: 0.75, lineHeight: 1.45 }}>{empty}</Typography>
			</>
			: <>
				<Typography sx={{ fontSize: 22, fontWeight: 700, color: dsColorNew.textTitle, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
					{money(value, code)}<Box component="small" sx={{ fontSize: 13, fontWeight: 500, color: dsColorNew.textMuted, ml: 0.75 }}>{STAT_LABEL[stat]}</Box>
				</Typography>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textDark, mt: 0.75, lineHeight: 1.45 }}><b>{pricesCount(stats.n)}</b>, {captured(stats)}</Typography>
			</>}
	</Box>
}

const EmptyCell = () => <td style={{ textAlign: 'right', color: dsColorNew.textMuted }}>—</td>

function ChangeCell({ a, b, code }) {
	if (a === null || a === undefined || b === null || b === undefined) return <EmptyCell/>
	const e = a - b, r = Math.round(e)
	const color = r < 0 ? dsColorNew.successText : r > 0 ? dsColorNew.dangerRed : dsColorNew.textMuted
	return <td style={{ color, fontWeight: 600, textAlign: 'right' }}>{r === 0 ? '= 0' : `${r < 0 ? '▼' : '▲'} ${signedPct(e / b)} · ${signedMoney(e, code)}`}</td>
}

function Mover({ mover, kind, code }) {
	const label = kind === 'rise' ? 'Biggest rise' : 'Biggest drop'
	return <Box sx={{ ...box, display: 'flex', alignItems: 'center', gap: 1.25, p: '10px 12px' }}>
		{!!mover && <AppBrandMark name={mover.provider.name} size={20}/>}
		<Box>
			<Typography sx={overline}>{label}</Typography>
			{mover
				? <Typography sx={{ fontSize: 14, fontWeight: 700, color: dsColorNew.textTitle }}>{mover.provider.name}{mover.thin && <Box component="span" sx={{ ml: 0.75, px: 1, borderRadius: '999px', fontSize: 12, fontWeight: 500, backgroundColor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber }}>few prices</Box>}</Typography>
				: <Typography sx={{ fontSize: 14, fontWeight: 500, color: dsColorNew.textSubtle }}>Nobody {kind === 'rise' ? 'went up' : 'went down'}</Typography>}
		</Box>
		{!!mover && <Box sx={{ ml: 'auto', textAlign: 'right', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', color: kind === 'rise' ? dsColorNew.dangerRed : dsColorNew.successText }}>
			{kind === 'rise' ? '▲' : '▼'} {signedMoney(mover.e, code)}<br/><span style={{ fontWeight: 600 }}>{signedPct(mover.p)}</span>
		</Box>}
	</Box>
}

/**
 * The detail of a cell (CA 15): prices and capture days of both periods, Min / Median / Max side by side, who moved
 * most on that date, and the two ways into Advanced (BI). Esc closes and gives the focus back to the cell. Everything
 * is computed among the rows of `model`, which are the providers shown (E3).
 *
 * Sections, top to bottom: few-prices note · the two periods · `children` (the price curve of the cell, E9: it loads on
 * its own while the rest is painted at once) · Min / Median / Max · who moved most.
 */
export default function DetailDialog({ open, target, model, headers, view, code, officeName, filters = {}, onClose, onExplorer, onPriceCurve = null, children }) {
	if (!open || !target) return null
	const { rowKey, j } = target
	const isMedian = rowKey === MEDIAN
	const row = isMedian ? null : model.rows.find(r => r.provider.id === rowKey)
	const cell = isMedian ? model.median[j] : row?.cells[j]
	if (!cell?.cur) return null

	const column = model.columns[j]
	const header = headers[j]
	const isWeek = header.isWeek
	const name = isMedian ? 'Market median' : row.provider.name
	const when = isWeek ? `week of ${fmtDay(header.first)}` : fmtWeekday(header.first)
	const cur = cell.cur, prev = cell.prev
	const thinCur = cur.n < 5, thinPrev = !!prev && prev.n < 5

	const prevDates = (column.prevDates || []).map(fromIso).filter(Boolean)
	const prevWhen = prevDates.length > 1 ? ` · ${fmtRange(prevDates[0], prevDates[prevDates.length - 1])}` : prevDates.length ? ` · ${fmtWeekday(prevDates[0])}` : ''
	const prevTitle = view.cmp === 'ly' ? `${view.same ? 'Last year · same date' : 'Last year · same weekday'}${prevWhen}` : PREV_COLUMN[view.cmp]
	const nowTitle = `Now · ${isWeek ? fmtRange(header.first, header.last) : fmtWeekday(header.first)}`
	const noPrevWhy = cell.why === 'passed'
		? 'This pick-up date had already gone by then: there is nothing newer to compare.'
		: view.cmp === 'ly' ? 'Your company captured no prices for this provider and date last year.' : 'No prices captured in the comparison period.'

	const moved = movers(model.rows, j)
	const explorer = BI_ROUTES.TITLE
	const subtitle = `${officeName} · ${view.lor} days · ${STAT_LABEL[view.stat]} per provider · ${cmpPhrase(view.cmp, view.same)}`
		+ detailSubtitleTail({ isMedian, model, filters })

	const mmmRow = (label, key) => <tr key={key}>
		<td>{label}</td>
		<td style={{ textAlign: 'right' }}>{money(cur[key], code)}</td>
		{prev ? <td style={{ textAlign: 'right' }}>{money(prev[key], code)}</td> : <EmptyCell/>}
		{prev ? <ChangeCell a={cur[key]} b={prev[key]} code={code}/> : <EmptyCell/>}
	</tr>

	return <AppDialog open={open} onClose={onClose} maxWidth="md" aria-labelledby="season-detail-title">
		<AppDialogHeader
			icon={isMedian ? 'chart-column' : 'car'}
			title={<span id="season-detail-title">{name} · {when}</span>}
			subtitle={subtitle}
			onClose={onClose}
		/>
		<AppDialogBody sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			{(thinCur || thinPrev) && <AppNote severity="warning" sx={{ mb: 1.5 }}>
				Few prices behind this cell{thinPrev ? ` ${CMP_WORD[view.cmp]}` : ''}: the change may come from what was being watched, not from a price move.
			</AppNote>}
			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1, mb: 2 }}>
				<Period title={nowTitle} stats={cur} value={cell.val} stat={view.stat} code={code} thin={thinCur}/>
				<Period title={prevTitle} stats={prev} value={cell.prevVal} stat={view.stat} code={code} thin={thinPrev} empty={prev ? null : noPrevWhy}/>
			</Box>
			{typeof children === 'function' ? children({ rowKey, j, isMedian, row, cell, column, header }) : children}
			<Box component="table" sx={{
				width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13,
				'& th': { backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)', fontSize: 14, fontWeight: 700, textAlign: 'right', px: 2, py: 1 },
				'& th:first-of-type': { textAlign: 'left', borderTopLeftRadius: 8 }, '& th:last-of-type': { borderTopRightRadius: 8 },
				'& td': { px: 2, py: 1.25, borderBottom: `1px solid ${dsColorNew.grayBorder}`, color: dsColorNew.textDark, fontVariantNumeric: 'tabular-nums' },
			}}>
				<thead><tr><th>{isMedian ? 'Competitors’ prices' : 'Prices behind the cell'}</th><th>Now</th><th>{PREV_COLUMN[view.cmp]}</th><th>Change</th></tr></thead>
				<tbody>{mmmRow('Min', 'min')}{mmmRow('Median', 'median')}{mmmRow('Max', 'max')}</tbody>
			</Box>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mt: 2.25, mb: 1 }}>
				Who moved most {isWeek ? 'this week' : 'on this date'}{model.filtered && <Box component="span" sx={{ fontWeight: 400, color: dsColorNew.textMuted }}> · among the providers shown</Box>}
			</Typography>
			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
				<Mover mover={moved.drop} kind="drop" code={code}/>
				<Mover mover={moved.rise} kind="rise" code={code}/>
			</Box>
		</AppDialogBody>
		<AppDialogFooter>
			{!!onPriceCurve && <Button variant="text" onClick={() => onPriceCurve(j)} startIcon={<IconSeeker icon="chart-line" sx={{ fontSize: 13 }}/>} sx={{ textTransform: 'none' }}>
				Price curve of this date
			</Button>}
			<Button variant="outlined" onClick={() => onExplorer('cell', j)} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>
				Open in {explorer}
			</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}
